import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Grid, IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import { HistoryEdu, Schedule, ShareLocation } from '@mui/icons-material';
import Copy from 'components/common/Copy';
import Date from 'components/formaters/Date';
import ShareButton from 'components/common/ShareButton';
import { CarModel, StockStatus } from 'models/StockModel';
import { TransportationStatus } from 'models/TransportationModel';
import useCarModals from 'modules/cars/hooks/useCarModals';
import Overstay from 'modules/cars/common/Overstay';
import ViewYardInstructions from 'modules/yards/common/ViewYardInstructions';
import auth from 'recoil/atoms/auth';


type YardCardProps = {
    car: CarModel,
}

const transitable = [
    TransportationStatus.NonAssigned,
    TransportationStatus.Pending,
    TransportationStatus.InTransit,
];

const YardCard = ({car}: YardCardProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const {openYardModal, openYardInSingleModal, openYardActivity, confirmCancelTransportation} = useCarModals();

    const currentTransit = car.transportations?.find(tr => transitable.includes(tr.status));

    const canMove = Boolean(!car.yard || car.yard.dateIn) &&
        [StockStatus.Pending, StockStatus.Ready].includes(car.status);

    return (
        <Card sx={{mb: 2}}>
            <CardHeader
                title={t('yard')}
                action={my.permissions.has('cars manage') && car.yards.length > 0 && (
                    <IconButton
                        aria-label="Show activity"
                        onClick={() => openYardActivity(car.yards)}
                    >
                        <HistoryEdu/>
                    </IconButton>
                )}
            />
            <CardContent>
                {car.yard ?
                    <Box>
                        <Grid container spacing={1}>
                            <Grid item display="flex" alignItems="center">
                                <Box sx={{mr: 1}}>
                                    {car.yard.port} {car.yard.name}
                                </Box>
                                {!car.yard.arrived &&
                                    <Tooltip title={t('in_transit')} placement="top">
                                        <Schedule color="error" fontSize="small"/>
                                    </Tooltip>
                                }
                            </Grid>
                            <Grid
                                item xs={9}
                                sx={{fontSize: 'smaller', color: 'text.disabled'}}
                            >
                                {car.yard.address}
                            </Grid>
                            <Grid item xs={3} textAlign="right">
                                <Tooltip
                                    title={t('copy_address')}
                                    placement="top"
                                >
                                    <span>
                                        <Copy value={car.yard.address}/>
                                    </span>
                                </Tooltip>
                                {' '}
                                <Tooltip
                                    title={t('share_location')}
                                    placement="top"
                                >
                                    <span>
                                        <ShareButton
                                            icon={ShareLocation}
                                            size="sm"
                                            title={t('location')}
                                            url={`https://www.google.com/maps/search/?api=1&query=${car.yard.coordinates.latitude}%2C${car.yard.coordinates.longitude}`}
                                        />
                                    </span>
                                </Tooltip>
                                {' '}
                                <Tooltip
                                    title={t('instructions')}
                                    placement="top"
                                >
                                    <span>
                                        <ViewYardInstructions
                                            yard={car.yard}
                                        />
                                    </span>
                                </Tooltip>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} sx={{color: !car.yard.dateIn && (car.yard.occupied / car.yard.capacity > .9) ? 'error.main' : undefined}}>
                            {car.yard.occupied} {t('of')} {car.yard.capacity}
                        </Grid>

                        {car.yard.dateIn && <>
                            <Typography
                                component="span"
                                fontSize="smaller"
                                color="text.disabled"
                            >
                                {t('arrived_at')}
                            </Typography>: <Date date={car.yard.dateIn}/>
                            {' '}
                            <Overstay
                                car={car}
                                withTitle
                            />
                        </>}
                    </Box> :
                    <Typography>{t('unknown')}</Typography>
                }
            </CardContent>

            {currentTransit && (
                <CardContent>
                    {t('in_transit_from')} {currentTransit.from.name}
                </CardContent>
            )}

            {my.permissions.has('cars manage yardin') &&
                <CardActions>
                    {car.yard && !car.yard.dateIn &&
                        <Button
                            color="success"
                            size="small"
                            sx={{ml: 1}}
                            onClick={() => openYardInSingleModal(car)}
                        >
                            {t('arrived')}
                        </Button>
                    }

                    {canMove &&
                        <Button
                            onClick={() => openYardModal([car])}
                            size="small"
                        >
                            {car.yard ? t('move_to_yard') : t('select_yard')}
                        </Button>
                    }

                    {currentTransit && (
                        <Button
                            onClick={() => confirmCancelTransportation(currentTransit, car)}
                            size="small"
                        >
                            {t('cancel_transportation')}
                        </Button>
                    )}
                </CardActions>
            }
        </Card>
    );
};

export default YardCard;
